/**
 * Open while the mouse rests on it, close a moment after it leaves: the left menu's "Icons only" (MB, 25 Oct 2026),
 * Teslyn Cloud's copy of Teslyn Field's hook (apps/field/src/lib/useHoverOpen.ts; keep the two the same). It opens
 * `MENU_OPEN_DELAY_MS` after the mouse comes in, so passing over it on the way to the page does not open it, and
 * closes `MENU_CLOSE_DELAY_MS` after the mouse leaves, so a slip past its edge does not close it (`@teslyn/domain`
 * side-menu.ts). A finger's tap is not hover: the caller handles touch. Switched off, it is closed and any waiting
 * delay is dropped.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { MENU_CLOSE_DELAY_MS, MENU_OPEN_DELAY_MS } from '@teslyn/domain';

export interface HoverOpen {
  readonly open: boolean;
  readonly onPointerEnter: (e: PointerEvent) => void;
  readonly onPointerLeave: (e: PointerEvent) => void;
  /** Close now (Escape, an item chosen), dropping a waiting delay. */
  readonly close: () => void;
  /** Open now (a click or a tap on it), dropping a waiting delay; the mouse leaving closes it as usual. */
  readonly openNow: () => void;
}

export function useHoverOpen(enabled: boolean): HoverOpen {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const on = useRef(enabled);
  on.current = enabled;
  // switched off (another menu mode, the panel pinned): closed, nothing waiting
  const [was, setWas] = useState(enabled);
  if (was !== enabled) {
    setWas(enabled);
    setOpen(false);
  }
  useEffect(() => () => window.clearTimeout(timer.current), [enabled]);
  const later = useCallback((ms: number, value: boolean): void => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = undefined;
      setOpen(value);
    }, ms);
  }, []);
  const onPointerEnter = useCallback(
    (e: PointerEvent): void => {
      if (on.current && e.pointerType !== 'touch') later(MENU_OPEN_DELAY_MS, true);
    },
    [later],
  );
  const onPointerLeave = useCallback(
    (e: PointerEvent): void => {
      if (on.current && e.pointerType !== 'touch') later(MENU_CLOSE_DELAY_MS, false);
    },
    [later],
  );
  const close = useCallback((): void => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    setOpen(false);
  }, []);
  const openNow = useCallback((): void => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    if (on.current) setOpen(true);
  }, []);
  const shown = enabled && open;
  return useMemo(
    () => ({ open: shown, onPointerEnter, onPointerLeave, close, openNow }),
    [shown, onPointerEnter, onPointerLeave, close, openNow],
  );
}
